import { Box, Alert, keyframes } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

interface Props {
  isCorrectlyPositioned?: boolean;
  guidanceMessage?: string;
}

export const TestStripOverlay = ({ isCorrectlyPositioned, guidanceMessage }: Props) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: { xs: 2, sm: 2.5, md: 3 },
        background: 'rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Guidance Messages */}
      <Box sx={{ 
        width: '80%',
        maxWidth: '400px',
        mb: { xs: 1, sm: 1.5, md: 2 },
        zIndex: 1000,
        position: 'relative'
      }}>
        <Alert 
          severity={isCorrectlyPositioned ? "success" : "info"}
          icon={isCorrectlyPositioned ? <CheckCircleIcon /> : undefined}
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            py: { xs: 0.5, sm: 0.75, md: 1 },
            px: { xs: 1, sm: 1.5, md: 2 },
            '& .MuiAlert-message': {
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              fontWeight: 600,
              textAlign: 'center',
              width: '100%',
              color: isCorrectlyPositioned ? '#2E7D32' : '#0d47a1'
            },
            '& .MuiAlert-icon': {
              color: isCorrectlyPositioned ? '#2E7D32' : '#0d47a1',
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
            },
            '& .MuiAlert-action': {
              paddingTop: 0
            }
          }}
        >
          {guidanceMessage || "Position the test strip within the frame"}
        </Alert>
      </Box>

      {/* Vertical rectangle for test strip */}
      <Box
        sx={{
          position: 'relative',
          width: '12%',
          height: { xs: '65%', sm: '70%', md: '72%' },
          mb: { xs: 3, sm: 4, md: 5 },
          border: '2px solid white',
          borderRadius: 1,
          zIndex: 10,
          background: 'transparent',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
          animation: isCorrectlyPositioned ? `${pulse} 2s infinite` : 'none',
          transition: 'all 0.3s ease-in-out',
          mx: 'auto',
          mt: { xs: 0.5, sm: 0.75, md: 1 },
        }}
      >
        {/* Center line guide */}
        <Box sx={{
          position: 'absolute',
          left: '50%',
          height: '100%',
          borderLeft: '2px dashed rgba(255, 255, 255, 0.5)',
          borderStyle: 'dashed',
          borderWidth: '2px',
          borderColor: 'rgba(255, 255, 255, 0.7)',
          transform: 'translateX(-50%)'
        }} />
      </Box>

      {/* Spacer for bottom area */}
      <Box sx={{ height: { xs: '70px', sm: '60px', md: '60px' } }} />
    </Box>
  );
}; 